import SearchResultsDropdown from "@/components/search/SearchResultsDropdown";
import { SessionDetailModal } from "@/components/SessionDetailModal";
import { StudentDetailModal } from "@/components/StudentDetailModal";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/hooks/useNotifications";
import { useSearch } from "@/hooks/useSearch";
import { cn } from "@/lib/utils";
import { useAppStore } from "@/store/AppStore";
import {
  Bell,
  LogOut,
  Menu,
  Search,
  User,
  X,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { navigate } from "wouter/use-browser-location";

interface HeaderProps {
  onMenuToggle: () => void;
  isSidebarOpen: boolean;
}

export const Header: React.FC<HeaderProps> = ({ onMenuToggle }) => {
  const { user, counselorProfile, profilePictureUrl, logout } = useAuth();
  const { state } = useAppStore();
  const { data: notificationsData } = useNotifications(user?.id || "", {
    limit: "5",
    offset: "0",
  });
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);

  const searchRef = useRef<HTMLDivElement>(null);
  const searchState = useSearch();

  const handleLogout = () => {
    logout();
    setShowProfileDropdown(false);
  };

  // Handle clicks outside search dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        searchState.setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchState]);

  const handleSearchClose = () => {
    searchState.setIsOpen(false);
  };

  const handleStudentClick = (student: any) => {
    setSelectedStudent(student);
    setShowStudentModal(true);
  };

  const handleSessionClick = (session: any) => {
    setSelectedSession(session);
    setShowSessionModal(true);
  };

  // Use real-time notification count from AppStore
  const unreadNotifications = state.unreadCount || 0;

  // Get recent notifications for dropdown (limit to 3 most recent)
  const recentNotifications = notificationsData?.slice(0, 3) || [];

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440)
      return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  //

  return (
    <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-40">
      <div className="flex items-center justify-between">
        {/* Left side - Menu toggle and search */}
        <div className="flex items-center space-x-4 flex-1">
          <button
            onClick={onMenuToggle}
            className={cn(
              "p-2 rounded-lg hover:bg-gray-100 transition-colors",
              "lg:hidden" // Only show on mobile
            )}
          >
            <Menu className="h-5 w-5 text-gray-600" />
          </button>

          {/* Search */}
          <div className="relative flex-1 max-w-md" ref={searchRef}>
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 z-10" />
            <input
              type="text"
              placeholder="Search connected students, sessions..."
              value={searchState.query}
              onChange={(e) => searchState.setQuery(e.target.value)}
              className={cn(
                "w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg",
                "focus:ring-2 focus:ring-primary focus:border-transparent",
                "bg-gray-50 text-sm placeholder-gray-500"
              )}
              autoComplete="off"
            />
            {searchState.query && (
              <button
                onClick={() => {
                  searchState.setQuery("");
                  searchState.setIsOpen(false);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors z-10"
                type="button"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <SearchResultsDropdown
              searchState={searchState}
              onClose={handleSearchClose}
              onStudentClick={handleStudentClick}
              onSessionClick={handleSessionClick}
            />
          </div>
        </div>

        {/* Center - Quick stats (hidden on mobile) */}
        {/* <div className="hidden xl:flex items-center space-x-6 mx-8">
          <Link
            href="/sessions"
            className="flex items-center space-x-2 px-3 py-2 bg-blue-50 rounded-lg"
          >
            <MessageSquare className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              {todaysStats.activeSessions} Active
            </span>
          </Link>
          <Link
            href="/sessions"
            className="flex items-center space-x-2 px-3 py-2 bg-green-50 rounded-lg"
          >
            <Calendar className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-green-900">
              {todaysStats.scheduledSessions} Scheduled
            </span>
          </Link>
          <Link
            href="/sessions"
            className="flex items-center space-x-2 px-3 py-2 bg-purple-50 rounded-lg"
          >
            <TrendingUp className="h-4 w-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-900">
              {todaysStats.newMessages} Messages
            </span>
          </Link>
        </div> */}

        {/* Right side - Notifications and profile */}
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Bell className="h-5 w-5 text-gray-600" />
              {unreadNotifications > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {unreadNotifications > 9 ? "9+" : unreadNotifications}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowNotifications(false)}
                />
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900">
                        Notifications
                      </h3>
                      <Link href="/notifications">
                        <span className="text-sm text-primary hover:text-primary/80 cursor-pointer">
                          View all
                        </span>
                      </Link>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {recentNotifications.length > 0 ? (
                      recentNotifications.map((notification, index) => (
                        <div
                          key={notification.id}
                          className={cn(
                            "p-3 hover:bg-gray-50 cursor-pointer",
                            index < recentNotifications.length - 1 &&
                              "border-b border-gray-100",
                            !notification.isRead && "bg-blue-50/30"
                          )}
                          onClick={() => {
                            setShowNotifications(false);
                            navigate("/notifications");
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p
                                className={cn(
                                  "text-sm font-medium",
                                  !notification.isRead
                                    ? "text-gray-900"
                                    : "text-gray-700"
                                )}
                              >
                                {notification.title}
                              </p>
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {formatRelativeTime(notification.createdAt)}
                              </p>
                            </div>
                            {!notification.isRead && (
                              <div className="w-2 h-2 bg-primary rounded-full ml-2 mt-1 flex-shrink-0"></div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-6 text-center text-gray-500">
                        <Bell className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm">No notifications yet</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Profile */}
          <div className="relative">
            <button
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center overflow-hidden">
                {profilePictureUrl ? (
                  <img
                    src={profilePictureUrl}
                    alt="Profile"
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <User className="h-4 w-4 text-white" />
                )}
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-gray-900">
                  {counselorProfile &&
                  counselorProfile.firstName &&
                  counselorProfile.lastName
                    ? `${counselorProfile.firstName} ${counselorProfile.lastName}`.trim()
                    : user?.name || "Counselor"}
                </p>
                <p className="text-xs text-gray-600 max-w-[170px] truncate">
                  {counselorProfile?.specialization || "Career Counselor"}
                </p>
              </div>
            </button>

            {/* Profile Dropdown */}
            {showProfileDropdown && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowProfileDropdown(false)}
                />
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-200">
                    <p className="font-medium text-gray-900">
                      {counselorProfile &&
                      counselorProfile.firstName &&
                      counselorProfile.lastName
                        ? `${counselorProfile.firstName} ${counselorProfile.lastName}`.trim()
                        : user?.name || "Counselor"}
                    </p>
                    <p className="text-sm text-gray-600">
                      {user?.phoneNumber ||
                        counselorProfile?.phoneNumber ||
                        "No phone number"}
                    </p>
                  </div>
                  <div className="py-2">
                    <Link href="/profile">
                      <button
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                        onClick={() => setShowProfileDropdown(false)}
                      >
                        <User className="h-4 w-4" />
                        <span>My Profile</span>
                      </button>
                    </Link>
                    {/* <button
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                      onClick={() => setShowProfileDropdown(false)}
                    >
                      <Settings className="h-4 w-4" />
                      <span>Settings</span>
                    </button> */}
                    <hr className="my-2" />
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modals */}
      <StudentDetailModal
        student={selectedStudent}
        isOpen={showStudentModal}
        onClose={() => {
          setShowStudentModal(false);
          setSelectedStudent(null);
        }}
      />

      <SessionDetailModal
        session={selectedSession}
        isOpen={showSessionModal}
        onClose={() => {
          setShowSessionModal(false);
          setSelectedSession(null);
        }}
      />
    </header>
  );
};
